import { PERMISSION_KEYS as P } from "./permissionKeys";

/** Fallback when a key has no entry (shouldn’t happen for known keys). */
export const DEFAULT_PAGE_DENIED = {
  title: "You don’t have permission",
  description:
    "You don’t have permission to open this page. Contact your administrator if you need access.",
  buttonLabel: "Go to Dashboard",
  to: "/dashboard",
};

export const DEFAULT_MODAL_DENIED = {
  title: "Action not allowed",
  description:
    "You don’t have permission to perform this action. Contact your administrator if you need access.",
};

/**
 * Full-page 403 copy — one entry per permission key (route / whole tab).
 */
export const PAGE_DENIED_BY_KEY = {
  [P.DASHBOARD_VIEW]: {
    title: "Dashboard — access restricted",
    description:
      "You don’t have permission to view the dashboard. Contact your administrator if you need access.",
  },
  [P.BILLING_VIEW]: {
    title: "Billing — access restricted",
    description:
      "You don’t have permission to open Billing configuration. SaaS export, QuickBooks export, and month locking use separate permissions.",
  },
  [P.SAAS_BILLING_VIEW]: {
    title: "SaaS billing export — access restricted",
    description:
      "You don’t have permission to open SaaS module billing and export. Contact your administrator if you need access.",
  },
  [P.QB_BILLING_VIEW]: {
    title: "QuickBooks export — access restricted",
    description:
      "You don’t have permission to open QuickBooks exports. Contact your administrator if you need access.",
  },
  [P.REPORTS_LOCK_AUDIT_VIEW]: {
    title: "Month locking & audit — access restricted",
    description:
      "You don’t have permission to open month locking or the audit log. Contact your administrator if you need access.",
  },
  [P.SETTINGS_ROLE_PERMISSION]: {
    title: "Role & permission — access restricted",
    description:
      "You don’t have permission to manage roles and permissions. Only authorized roles can open this settings area.",
  },
  [P.TAGS_VIEW]: {
    title: "Tag mapping — access restricted",
    description:
      "You don’t have permission to view or edit client or vendor tag mapping. Contact your administrator if you need access.",
  },
  [P.SYSTEM_LIST_ADD]: {
    title: "Add system list item — access restricted",
    description: "You don’t have permission to add client or vendor tags on System lists.",
  },
  [P.SYSTEM_LIST_EDIT]: {
    title: "Edit system list item — access restricted",
    description: "You don’t have permission to edit client or vendor tags on System lists.",
  },
  [P.SYSTEM_LIST_DELETE]: {
    title: "Delete system list item — access restricted",
    description: "You don’t have permission to delete client or vendor tags on System lists.",
  },
  [P.SAAS_MODULE_ADD]: {
    title: "Add SaaS module — access restricted",
    description:
      "You don’t have permission to add SaaS modules on System lists. Contact your administrator if you need access.",
  },
  [P.SAAS_MODULE_EDIT]: {
    title: "Edit SaaS module — access restricted",
    description:
      "You don’t have permission to edit SaaS modules on System lists. Contact your administrator if you need access.",
  },
  [P.SAAS_MODULE_DELETE]: {
    title: "Delete SaaS module — access restricted",
    description:
      "You don’t have permission to delete SaaS modules on System lists. Contact your administrator if you need access.",
  },
  [P.PROJECTS_SETTINGS_MANAGE]: {
    title: "Project settings — access restricted",
    description:
      "You don’t have permission to open this project’s Settings tab (workflow, notifications, and related options). Contact your administrator if you need access.",
  },
  [P.USERS_VIEW]: {
    title: "Users — access restricted",
    description: "You don’t have permission to view the Users list or user-related pages.",
  },
  [P.USERS_CREATE]: {
    title: "Create user — access restricted",
    description: "You don’t have permission to add new users.",
  },
  [P.USERS_EDIT]: {
    title: "Edit user — access restricted",
    description: "You don’t have permission to edit user profiles or roles.",
  },
  [P.USERS_DELETE]: {
    title: "Delete user — access restricted",
    description: "You don’t have permission to delete users.",
  },
  [P.CLIENT_VIEW]: {
    title: "Clients — access restricted",
    description:
      "You don’t have permission to view the Clients list. Contact your administrator if you need access.",
  },
  [P.CLIENTS_SIDEBAR]: {
    title: "Clients menu — access restricted",
    description:
      "You don’t have permission to show the Clients entry in the sidebar. You may still open client areas from links if you have list access.",
  },
  [P.CLIENTS_DASHBOARD_VIEW]: {
    title: "Client dashboard — access restricted",
    description:
      "You don’t have permission to open a client’s dashboard. You may still use the Clients list if you have access.",
  },
  [P.CLIENTS_CREATE]: {
    title: "Create client — access restricted",
    description: "You don’t have permission to create new clients.",
  },
  [P.CLIENTS_EDIT]: {
    title: "Edit client — access restricted",
    description: "You don’t have permission to edit client records.",
  },
  [P.CLIENTS_DELETE]: {
    title: "Delete client — access restricted",
    description: "You don’t have permission to delete clients.",
  },
  [P.TEAMS_VIEW]: {
    title: "Teams — access restricted",
    description: "You don’t have permission to view the Teams area.",
  },
  [P.TEAMS_CREATE]: {
    title: "Create team — access restricted",
    description: "You don’t have permission to create teams.",
  },
  [P.TEAMS_EDIT]: {
    title: "Edit team — access restricted",
    description: "You don’t have permission to edit teams or team members.",
  },
  [P.TEAMS_DELETE]: {
    title: "Delete team — access restricted",
    description: "You don’t have permission to delete teams.",
  },
  [P.TEAMS_ADD_MEMBER]: {
    title: "Add team member — access restricted",
    description: "You don’t have permission to add members to teams.",
  },
  [P.PROJECTS_VIEW]: {
    title: "Projects — access restricted",
    description: "You don’t have permission to view the project list or open a project workspace.",
  },
  [P.PROJECTS_SIDEBAR]: {
    title: "Projects menu — access restricted",
    description:
      "You don’t have permission to show the Projects entry in the sidebar. You may still open projects from links if you have list access.",
  },
  [P.PROJECTS_DASHBOARD_VIEW]: {
    title: "Project dashboard — access restricted",
    description:
      "You don’t have permission to view this project’s dashboard. You may still use other project tabs if you have access.",
  },
  [P.PROJECTS_CREATE]: {
    title: "Create project — access restricted",
    description: "You don’t have permission to create new projects.",
  },
  [P.PROJECTS_EDIT]: {
    title: "Edit project — access restricted",
    description: "You don’t have permission to edit project details.",
  },
  [P.PROJECTS_DELETE]: {
    title: "Delete project — access restricted",
    description: "You don’t have permission to delete projects.",
  },
  [P.WORK_ITEMS_VIEW]: {
    title: "Work Items — access restricted",
    description:
      "You don’t have permission to view the full work items list. Contact your administrator if you need access.",
  },
  [P.KANBAN_VIEW]: {
    title: "Kanban board — access restricted",
    description:
      "You don’t have permission to view the Kanban board. Contact your administrator if you need access.",
  },
  [P.MY_WORK_ITEMS_VIEW]: {
    title: "My work items — access restricted",
    description:
      "You don’t have permission to view work items assigned to you. Contact your administrator if you need access.",
  },
  [P.WORK_ITEMS_CREATE]: {
    title: "Create work item — access restricted",
    description: "You don’t have permission to create work items.",
  },
  [P.WORK_ITEMS_EDIT]: {
    title: "Edit work item — access restricted",
    description: "You don’t have permission to edit work items.",
  },
  [P.WORK_ITEMS_DELETE]: {
    title: "Delete work item — access restricted",
    description: "You don’t have permission to delete work items.",
  },
  [P.TIME_ENTRY_TIMESHEET_VIEW]: {
    title: "My Timesheet — access restricted",
    description:
      "You don’t have permission to view your timesheet list. Contact your administrator if you need access.",
  },
  [P.ALL_TIME_ENTRY_TIMESHEET_VIEW]: {
    title: "All Time Entries — access restricted",
    description:
      "You don’t have permission to view all time entries. Contact your administrator if you need access.",
  },
  [P.TIME_ENTRY_TEAM_SUMMARY_VIEW]: {
    title: "Team time summary — access restricted",
    description:
      "You don’t have permission to view the team time summary. Contact your administrator if you need access.",
  },
  [P.TIME_ENTRY_PROJECT_SUMMARY_VIEW]: {
    title: "Project time summary — access restricted",
    description:
      "You don’t have permission to view the project time summary. Contact your administrator if you need access.",
  },
  [P.TIME_ENTRY_CREATE]: {
    title: "Log time — access restricted",
    description: "You don’t have permission to log new time entries.",
  },
  [P.TIME_ENTRY_EDIT]: {
    title: "Edit time entry — access restricted",
    description: "You don’t have permission to edit time entries.",
  },
  [P.TIME_ENTRY_DELETE]: {
    title: "Delete time entry — access restricted",
    description: "You don’t have permission to delete time entries.",
  },
};

/** Short phrases for combining multiple required permissions on one route. */
const NEED_PHRASE = {
  [P.BILLING_VIEW]: "Billing configuration",
  [P.SAAS_BILLING_VIEW]: "SaaS billing export",
  [P.QB_BILLING_VIEW]: "QuickBooks export",
  [P.REPORTS_LOCK_AUDIT_VIEW]: "month locking & audit",
  [P.SAAS_BILLING_EXPORT]: "export SaaS billing",
  [P.QB_BILLING_EXPORT]: "export QuickBooks billing",
  [P.MONTH_LOCK_MANAGE]: "lock billing months",
  [P.MONTH_LOCK_UNLOCK]: "unlock billing months",
  [P.REPORTS_AUDIT_EXPORT]: "export the audit log",
  [P.SETTINGS_ROLE_PERMISSION]: "Role & permission settings",
  [P.TAGS_VIEW]: "Tag mapping access",
  [P.SYSTEM_LIST_ADD]: "add system list items",
  [P.SYSTEM_LIST_EDIT]: "edit system list items",
  [P.SYSTEM_LIST_DELETE]: "delete system list items",
  [P.SAAS_MODULE_ADD]: "add SaaS modules",
  [P.SAAS_MODULE_EDIT]: "edit SaaS modules",
  [P.SAAS_MODULE_DELETE]: "delete SaaS modules",
  [P.PROJECTS_SETTINGS_MANAGE]: "use project Settings (workflow, notifications, etc.)",
  [P.USERS_VIEW]: "view Users",
  [P.USERS_CREATE]: "create users",
  [P.USERS_EDIT]: "edit users",
  [P.USERS_DELETE]: "delete users",
  [P.CLIENT_VIEW]: "view Clients",
  [P.CLIENTS_SIDEBAR]: "show Clients in the sidebar",
  [P.CLIENTS_DASHBOARD_VIEW]: "view client dashboards",
  [P.CLIENTS_CREATE]: "create clients",
  [P.CLIENTS_EDIT]: "edit clients",
  [P.CLIENTS_DELETE]: "delete clients",
  [P.TEAMS_VIEW]: "view Teams",
  [P.TEAMS_CREATE]: "create teams",
  [P.TEAMS_EDIT]: "edit teams",
  [P.TEAMS_DELETE]: "delete teams",
  [P.TEAMS_ADD_MEMBER]: "add team members",
  [P.PROJECTS_VIEW]: "view Projects",
  [P.PROJECTS_SIDEBAR]: "show Projects in the sidebar",
  [P.PROJECTS_DASHBOARD_VIEW]: "view project dashboards",
  [P.PROJECTS_CREATE]: "create projects",
  [P.PROJECTS_EDIT]: "edit projects",
  [P.PROJECTS_DELETE]: "delete projects",
  [P.WORK_ITEMS_VIEW]: "view all work items",
  [P.KANBAN_VIEW]: "view the Kanban board",
  [P.MY_WORK_ITEMS_VIEW]: "view My work items",
  [P.WORK_ITEMS_CREATE]: "create work items",
  [P.WORK_ITEMS_EDIT]: "edit work items",
  [P.WORK_ITEMS_DELETE]: "delete work items",
  [P.TIME_ENTRY_TIMESHEET_VIEW]: "view My Timesheet",
  [P.ALL_TIME_ENTRY_TIMESHEET_VIEW]: "view All Time Entries",
  [P.TIME_ENTRY_TEAM_SUMMARY_VIEW]: "view Team time summary",
  [P.TIME_ENTRY_PROJECT_SUMMARY_VIEW]: "view Project time summary",
  [P.TIME_ENTRY_CREATE]: "log time",
  [P.TIME_ENTRY_EDIT]: "edit time entries",
  [P.TIME_ENTRY_DELETE]: "delete time entries",
};

/**
 * Copy for full-page 403 from one or more permission keys (e.g. route guard).
 * @param {string|string[]} permission
 * @param {boolean} [requireAll=true] — `false` means the route accepts any one key (OR); denied
 *   copy explains “at least one of…” when the user lacks every key.
 * @param {{ missingKeys?: string[] }} [options] — when set (e.g. from {@link RequirePermission}),
 *   copy is based on what the user actually lacks, so add/edit routes show create/edit-specific
 *   messages instead of a generic “two permissions” paragraph.
 */
export function getPageDeniedContent(permission, requireAll = true, options = {}) {
  const keys = Array.isArray(permission) ? permission : [permission];
  const base = { ...DEFAULT_PAGE_DENIED };
  const missingKeys = options.missingKeys;

  if (keys.length === 1) {
    const entry = PAGE_DENIED_BY_KEY[keys[0]];
    if (entry) {
      return { ...base, ...entry, to: base.to, buttonLabel: base.buttonLabel };
    }
    return base;
  }

  // Multi-key route: if exactly one permission is missing, use that key’s dedicated page copy
  // (e.g. add client needs VIEW + CREATE → missing only CREATE shows “Create client — …”).
  if (missingKeys != null && missingKeys.length === 1) {
    const entry = PAGE_DENIED_BY_KEY[missingKeys[0]];
    if (entry) {
      return { ...base, ...entry, to: base.to, buttonLabel: base.buttonLabel };
    }
  }

  // OR-route: user must have at least one key; denied only when they have none
  if (!requireAll && keys.length > 1 && missingKeys != null && missingKeys.length === keys.length) {
    const parts = keys.map((k) => NEED_PHRASE[k] || k);
    const list =
      parts.length === 2
        ? `${parts[0]} or ${parts[1]}`
        : parts.slice(0, -1).join(", ") + ", or " + parts[parts.length - 1];
    return {
      ...base,
      title: "You don’t have permission",
      description: `This page requires at least one of the following permissions: ${list}. Contact your administrator if you need access.`,
    };
  }

  const keysForList = missingKeys != null && missingKeys.length > 0 ? missingKeys : keys;
  const parts = keysForList.map((k) => NEED_PHRASE[k] || k);
  const list =
    parts.length === 2
      ? `${parts[0]} and ${parts[1]}`
      : parts.length === 1
        ? parts[0]
        : parts.slice(0, -1).join(", ") + ", and " + parts[parts.length - 1];

  return {
    ...base,
    title: "You don’t have permission",
    description: `This page requires permissions you don’t have: ${list}. Contact your administrator if you need access.`,
  };
}

/**
 * Modal copy — short title + description for a specific blocked action.
 */
export const MODAL_DENIED_BY_KEY = {
  [P.BILLING_VIEW]: {
    title: "Billing not available",
    description: "Your role can’t change billing configuration here.",
  },
  [P.SAAS_BILLING_EXPORT]: {
    title: "Can’t export SaaS billing",
    description: "You don’t have permission to export SaaS billing (CSV/XLSX or downloads).",
  },
  [P.QB_BILLING_EXPORT]: {
    title: "Can’t export QuickBooks billing",
    description: "You don’t have permission to export QuickBooks billing (CSV/XLSX or downloads).",
  },
  [P.MONTH_LOCK_MANAGE]: {
    title: "Can’t lock month",
    description: "You don’t have permission to lock billing months.",
  },
  [P.MONTH_LOCK_UNLOCK]: {
    title: "Can’t unlock month",
    description: "You don’t have permission to unlock billing months.",
  },
  [P.REPORTS_AUDIT_EXPORT]: {
    title: "Can’t export audit log",
    description: "You don’t have permission to export audit log data.",
  },
  [P.USERS_CREATE]: {
    title: "Can’t add user",
    description: "You don’t have permission to create new users.",
  },
  [P.USERS_EDIT]: {
    title: "Can’t edit user",
    description: "You don’t have permission to edit this user or change their role.",
  },
  [P.USERS_DELETE]: {
    title: "Can’t delete user",
    description: "You don’t have permission to delete users.",
  },
  [P.CLIENTS_CREATE]: {
    title: "Can’t add client",
    description: "You don’t have permission to create new clients.",
  },
  [P.CLIENTS_EDIT]: {
    title: "Can’t edit client",
    description: "You don’t have permission to edit client records.",
  },
  [P.CLIENTS_DELETE]: {
    title: "Can’t delete client",
    description: "You don’t have permission to delete clients.",
  },
  [P.CLIENTS_DASHBOARD_VIEW]: {
    title: "Client dashboard unavailable",
    description: "You don’t have permission to open this client’s dashboard.",
  },
  [P.TEAMS_CREATE]: {
    title: "Can’t create team",
    description: "You don’t have permission to create teams.",
  },
  [P.TEAMS_EDIT]: {
    title: "Can’t manage team",
    description: "You don’t have permission to edit teams or members.",
  },
  [P.TEAMS_DELETE]: {
    title: "Can’t delete team",
    description: "You don’t have permission to delete teams.",
  },
  [P.TEAMS_ADD_MEMBER]: {
    title: "Can’t add member",
    description: "You don’t have permission to add people to this team.",
  },
  [P.PROJECTS_DASHBOARD_VIEW]: {
    title: "Project dashboard unavailable",
    description: "You don’t have permission to view this project’s dashboard.",
  },
  [P.PROJECTS_CREATE]: {
    title: "Can’t create project",
    description: "You don’t have permission to create new projects.",
  },
  [P.PROJECTS_EDIT]: {
    title: "Can’t edit project",
    description: "You don’t have permission to edit this project.",
  },
  [P.PROJECTS_DELETE]: {
    title: "Can’t delete project",
    description: "You don’t have permission to delete projects.",
  },
  [P.WORK_ITEMS_VIEW]: {
    title: "Work Items not available",
    description: "You don’t have permission to view the full work items list.",
  },
  [P.KANBAN_VIEW]: {
    title: "Kanban not available",
    description: "You don’t have permission to view the Kanban board.",
  },
  [P.MY_WORK_ITEMS_VIEW]: {
    title: "My work items not available",
    description: "You don’t have permission to view work items assigned to you.",
  },
  [P.WORK_ITEMS_CREATE]: {
    title: "Can’t create work item",
    description: "You don’t have permission to create work items.",
  },
  [P.WORK_ITEMS_EDIT]: {
    title: "Can’t edit work item",
    description: "You don’t have permission to edit this work item.",
  },
  [P.WORK_ITEMS_DELETE]: {
    title: "Can’t delete work item",
    description: "You don’t have permission to delete work items.",
  },
  [P.TIME_ENTRY_TIMESHEET_VIEW]: {
    title: "Timesheet not available",
    description: "Your role can’t view the timesheet list.",
  },
  [P.ALL_TIME_ENTRY_TIMESHEET_VIEW]: {
    title: "All Time Entries not available",
    description: "Your role can’t view all time entries.",
  },
  [P.TIME_ENTRY_TEAM_SUMMARY_VIEW]: {
    title: "Team summary not available",
    description: "Your role can’t view the team time summary.",
  },
  [P.TIME_ENTRY_PROJECT_SUMMARY_VIEW]: {
    title: "Project summary not available",
    description: "Your role can’t view the project time summary.",
  },
  [P.TIME_ENTRY_CREATE]: {
    title: "Can’t log time",
    description: "You don’t have permission to log time entries.",
  },
  [P.TIME_ENTRY_EDIT]: {
    title: "Can’t edit time entry",
    description: "You don’t have permission to edit this time entry.",
  },
  [P.TIME_ENTRY_DELETE]: {
    title: "Can’t delete time entry",
    description: "You don’t have permission to delete time entries.",
  },
  [P.SYSTEM_LIST_ADD]: {
    title: "Can’t add tag",
    description: "You don’t have permission to add system list tags.",
  },
  [P.SYSTEM_LIST_EDIT]: {
    title: "Can’t edit tag",
    description: "You don’t have permission to edit system list tags.",
  },
  [P.SYSTEM_LIST_DELETE]: {
    title: "Can’t delete tag",
    description: "You don’t have permission to delete system list tags.",
  },
  [P.SAAS_MODULE_ADD]: {
    title: "Can’t add SaaS module",
    description: "You don’t have permission to add SaaS modules on System lists.",
  },
  [P.SAAS_MODULE_EDIT]: {
    title: "Can’t edit SaaS module",
    description: "You don’t have permission to edit SaaS modules on System lists.",
  },
  [P.SAAS_MODULE_DELETE]: {
    title: "Can’t delete SaaS module",
    description: "You don’t have permission to delete SaaS modules on System lists.",
  },
};

/**
 * Modal copy for a blocked action (button / inline control).
 * @param {string} permissionKey — one of {@link ./permissionKeys.js}
 * @param {{ title?: string, description?: string }} [overrides]
 */
export function getModalDeniedContent(permissionKey, overrides = {}) {
  const fromMap = MODAL_DENIED_BY_KEY[permissionKey] || PAGE_DENIED_BY_KEY[permissionKey];
  const base = fromMap
    ? { title: fromMap.title, description: fromMap.description }
    : { ...DEFAULT_MODAL_DENIED };
  return {
    title: overrides.title ?? base.title,
    description: overrides.description ?? base.description,
  };
}
